/**
 * AgentProfilePage Component
 * AIDLC Administrative Backend
 *
 * Full agent profile with:
 *   - Agent header (avatar, name, role, status, expertise)
 *   - Tabbed content: Instructions | Governance | Activity
 *   - Editable markdown instruction file
 *   - Per-agent and global governance rules
 *   - Quick actions: Chat, Assign Task
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgent } from '../../hooks/useAgents';
import { useBridge } from '../layout/MainLayout';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { getAgentInstructions, updateAgentInstructions, getGlobalGovernance, updateGlobalGovernance } from '../../services/agentService';
import { AgentActivityFeed } from './AgentActivityFeed';
import { AgentBacklogTab } from './AgentBacklogTab';
import { AgentAssetsTab } from './AgentAssetsTab';
import { AgentInboxTab } from './AgentInboxTab';
import { getGovernanceRules } from '../../services/governanceService';
import type { GovernanceRule } from '../../services/governanceService';
import { getTokenSessions } from '../../services/tokenService';
import type { SessionRecord } from '../../services/tokenService';

type TabId = 'instructions' | 'governance' | 'activity' | 'backlog' | 'usage' | 'assets' | 'inbox';

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  working: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  available: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-ldaja-blue' },
  waiting: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  away: { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
  offline: { bg: 'bg-gray-50', text: 'text-gray-400', dot: 'bg-gray-300' },
};

export function AgentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { agent, isLoading } = useAgent(id || '');
  const { isConnected } = useBridge();

  // Tabs
  const [activeTab, setActiveTab] = useState<TabId>('instructions');

  // Instructions state
  const [instructionContent, setInstructionContent] = useState('');
  const [instructionOriginal, setInstructionOriginal] = useState('');
  const [instructionLoading, setInstructionLoading] = useState(true);
  const [instructionSaving, setInstructionSaving] = useState(false);
  const [instructionLastModified, setInstructionLastModified] = useState<string | null>(null);
  const [instructionError, setInstructionError] = useState<string | null>(null);
  const [instructionSaved, setInstructionSaved] = useState(false);

  // Governance state
  const [governanceRules, setGovernanceRules] = useState<GovernanceRule[]>([]);
  const [globalGovContent, setGlobalGovContent] = useState('');
  const [globalGovOriginal, setGlobalGovOriginal] = useState('');
  const [globalGovLoading, setGlobalGovLoading] = useState(true);
  const [globalGovSaving, setGlobalGovSaving] = useState(false);
  const [showGlobalEditor, setShowGlobalEditor] = useState(false);

  // Usage / token state
  const [tokenSessions, setTokenSessions] = useState<SessionRecord[]>([]);

  const isDirty = instructionContent !== instructionOriginal;
  const isGlobalGovDirty = globalGovContent !== globalGovOriginal;

  // Computed usage breakdowns
  const usageStats = useMemo(() => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const cutoff24h = new Date(now - day).toISOString();
    const cutoff7d = new Date(now - 7 * day).toISOString();
    const cutoff30d = new Date(now - 30 * day).toISOString();

    const reduce = (sessions: SessionRecord[]) => ({
      tasks: sessions.length,
      tokens: sessions.reduce((s, r) => s + r.inputTokens + r.outputTokens, 0),
      cost: sessions.reduce((s, r) => s + r.cost, 0),
    });

    return {
      day: reduce(tokenSessions.filter(s => s.timestamp >= cutoff24h)),
      week: reduce(tokenSessions.filter(s => s.timestamp >= cutoff7d)),
      month: reduce(tokenSessions.filter(s => s.timestamp >= cutoff30d)),
      all: reduce(tokenSessions),
    };
  }, [tokenSessions]);

  // Load instructions
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setInstructionLoading(true);
      setInstructionError(null);
      try {
        const data = await getAgentInstructions(id!);
        if (!cancelled) {
          setInstructionContent(data.content);
          setInstructionOriginal(data.content);
          setInstructionLastModified(data.lastModified);
        }
      } catch (err) {
        if (!cancelled) setInstructionError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setInstructionLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Load governance
  useEffect(() => {
    async function load() {
      setGlobalGovLoading(true);
      try {
        const [rules, globalDoc] = await Promise.all([
          getGovernanceRules(),
          getGlobalGovernance(),
        ]);
        setGovernanceRules(rules);
        setGlobalGovContent(globalDoc.content);
        setGlobalGovOriginal(globalDoc.content);
      } catch {
        // non-critical
      } finally {
        setGlobalGovLoading(false);
      }
    }
    load();
  }, []);

  // Load token sessions for this agent
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      try {
        const sessions = await getTokenSessions({ agent: id!, days: 30 });
        if (!cancelled) setTokenSessions(sessions);
      } catch {
        // non-critical — usage tab just shows zeros
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Save instructions
  const saveInstructions = useCallback(async () => {
    if (!id || !isDirty) return;
    setInstructionSaving(true);
    setInstructionError(null);
    try {
      const result = await updateAgentInstructions(id, instructionContent);
      setInstructionOriginal(result.content);
      setInstructionLastModified(result.lastModified);
      setInstructionSaved(true);
      setTimeout(() => setInstructionSaved(false), 3000);
    } catch (err) {
      setInstructionError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setInstructionSaving(false);
    }
  }, [id, instructionContent, isDirty]);

  // Save global governance
  const saveGlobalGovernance = useCallback(async () => {
    if (!isGlobalGovDirty) return;
    setGlobalGovSaving(true);
    try {
      const result = await updateGlobalGovernance(globalGovContent);
      setGlobalGovOriginal(result.content);
    } catch {
      // error handling
    } finally {
      setGlobalGovSaving(false);
    }
  }, [globalGovContent, isGlobalGovDirty]);

  // Keyboard shortcut: Ctrl+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (activeTab === 'instructions' && isDirty) saveInstructions();
        if (showGlobalEditor && isGlobalGovDirty) saveGlobalGovernance();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab, isDirty, saveInstructions, showGlobalEditor, isGlobalGovDirty, saveGlobalGovernance]);

  if (isLoading) return <LoadingSpinner message="Loading agent profile..." />;
  if (!agent) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-medium">Agent not found</p>
        <Button variant="secondary" size="sm" onClick={() => navigate('/')} className="mt-3">Back to Dashboard</Button>
      </div>
    );
  }

  const sc = statusColors[agent.status] || statusColors.offline;
  const agentRules = governanceRules.filter(r =>
    r.appliesTo.length === 0 || r.appliesTo.includes(agent.id)
  );

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'instructions', label: 'Instructions' },
    { id: 'governance', label: 'Governance', count: agentRules.length },
    { id: 'activity', label: 'Activity' },
    { id: 'backlog', label: 'Backlog' },
    { id: 'assets', label: 'Assets' },
    { id: 'inbox', label: 'Inbox' },
    { id: 'usage', label: 'Usage', count: usageStats.month.tasks },
  ];

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-ldaja-blue transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M15 19l-7-7 7-7" /></svg>
        Back to Dashboard
      </button>

      {/* ===== AGENT HEADER ===== */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold flex-shrink-0"
            style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
          >
            {agent.name.charAt(0)}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl font-bold text-ldaja-black">{agent.name}</h1>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
                {agent.status}
              </span>
              {isConnected && <span className="text-[10px] text-green-500">Live</span>}
            </div>
            <p className="text-sm text-gray-600">{agent.fullName} &mdash; {agent.role}</p>
            {agent.catchphrase && (
              <p className="text-sm text-gray-400 italic mt-1">"{agent.catchphrase}"</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {agent.expertise.map((skill) => (
                <span key={skill} className="px-2 py-0.5 bg-ldaja-blue/10 text-ldaja-blue text-[10px] font-medium rounded">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button variant="primary" size="sm" onClick={() => navigate(`/chat?agent=${agent.id}`)}>
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              Chat
            </Button>
            <div className="text-[10px] text-gray-400 text-center">
              {agent.yearsExp}y exp
            </div>
          </div>
        </div>

        {/* Stats row — last 24 hours */}
        <div className="flex gap-6 mt-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-lg font-bold text-ldaja-black">{usageStats.day.tasks}</p>
            <p className="text-[10px] text-gray-400">Tasks (24h)</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-ldaja-black">{usageStats.day.tokens.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400">Tokens (24h)</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-ldaja-black">${usageStats.day.cost.toFixed(2)}</p>
            <p className="text-[10px] text-gray-400">Cost (24h)</p>
          </div>
          {agent.statusTask && (
            <div className="flex-1 text-right">
              <p className="text-xs text-gray-500">Current Task</p>
              <p className="text-sm font-medium text-ldaja-black truncate">{agent.statusTask}</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-ldaja-blue text-ldaja-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div className="p-6">

          {/* ===== INSTRUCTIONS TAB ===== */}
          {activeTab === 'instructions' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-ldaja-black">Agent Instructions</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Edit {agent.name}'s instruction file ({agent.id}.md)
                    {instructionLastModified && <> &middot; Last modified: {new Date(instructionLastModified).toLocaleString()}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isDirty && <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>}
                  {instructionSaved && <span className="text-xs text-green-600 font-medium">Saved</span>}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={saveInstructions}
                    disabled={!isDirty || instructionSaving}
                    isLoading={instructionSaving}
                  >
                    Save
                  </Button>
                </div>
              </div>

              {instructionError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-700">{instructionError}</p>
                </div>
              )}

              {instructionLoading ? (
                <LoadingSpinner message="Loading instructions..." size="sm" />
              ) : (
                <textarea
                  value={instructionContent}
                  onChange={(e) => setInstructionContent(e.target.value)}
                  className="w-full h-[500px] px-4 py-3 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue resize-y bg-gray-50"
                  placeholder="No instruction file found. Start writing agent instructions..."
                  spellCheck={false}
                />
              )}
              <p className="text-[10px] text-gray-400 mt-2">
                Press Ctrl+S to save. Changes are written directly to agents/{agent.id}.md
              </p>
            </div>
          )}

          {/* ===== GOVERNANCE TAB ===== */}
          {activeTab === 'governance' && (
            <div>
              {/* Agent-specific rules */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-ldaja-black mb-3">
                  Governance Rules Applying to {agent.name}
                </h3>
                {agentRules.length === 0 ? (
                  <p className="text-xs text-gray-400">No governance rules assigned to this agent.</p>
                ) : (
                  <div className="space-y-2">
                    {agentRules.map((rule) => (
                      <div key={rule.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-ldaja-black">{rule.name}</span>
                            <Badge variant={rule.isActive ? 'success' : 'neutral'} dot>{rule.isActive ? 'Active' : 'Inactive'}</Badge>
                            <Badge variant="info">{rule.type}</Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                        </div>
                        <span className="text-[10px] text-gray-400">P{rule.priority}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Global governance */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-ldaja-black">Global Governance Policy</h3>
                    <p className="text-xs text-gray-400">System-wide rules all agents must follow</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {showGlobalEditor && isGlobalGovDirty && (
                      <span className="text-xs text-amber-600 font-medium">Unsaved</span>
                    )}
                    {showGlobalEditor && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={saveGlobalGovernance}
                        disabled={!isGlobalGovDirty || globalGovSaving}
                        isLoading={globalGovSaving}
                      >
                        Save Policy
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowGlobalEditor(!showGlobalEditor)}
                    >
                      {showGlobalEditor ? 'Hide Editor' : 'Edit Policy'}
                    </Button>
                  </div>
                </div>

                {globalGovLoading ? (
                  <LoadingSpinner message="Loading governance..." size="sm" />
                ) : showGlobalEditor ? (
                  <textarea
                    value={globalGovContent}
                    onChange={(e) => setGlobalGovContent(e.target.value)}
                    className="w-full h-[400px] px-4 py-3 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue resize-y bg-gray-50"
                    spellCheck={false}
                  />
                ) : (
                  <div className="prose prose-sm max-w-none p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">{globalGovContent || 'No global governance policy defined yet.'}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== ACTIVITY TAB ===== */}
          {activeTab === 'activity' && (
            <AgentActivityFeed agentId={agent.id} agentName={agent.name} />
          )}

          {/* ===== BACKLOG TAB ===== */}
          {activeTab === 'backlog' && (
            <AgentBacklogTab agentId={agent.id} agentName={agent.name} />
          )}

          {/* ===== ASSETS TAB ===== */}
          {activeTab === 'assets' && (
            <AgentAssetsTab agentId={agent.id} agentName={agent.name} />
          )}

          {/* ===== INBOX TAB ===== */}
          {activeTab === 'inbox' && (
            <AgentInboxTab agentId={agent.id} agentName={agent.name} agentEmail={agent.email} />
          )}

          {/* ===== USAGE TAB ===== */}
          {activeTab === 'usage' && (
            <div className="p-6 space-y-6">
              {/* Period breakdown cards */}
              <div className="grid grid-cols-3 gap-4">
                {([
                  { label: 'This Week', data: usageStats.week },
                  { label: 'This Month', data: usageStats.month },
                  { label: 'All Time', data: usageStats.all },
                ] as const).map(({ label, data }) => (
                  <div key={label} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{label}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-500">Tasks</span>
                        <span className="text-lg font-bold text-ldaja-black">{data.tasks}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-500">Tokens</span>
                        <span className="text-lg font-bold text-ldaja-black">{data.tokens.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-500">Cost</span>
                        <span className="text-lg font-bold text-ldaja-black">${data.cost.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent sessions table */}
              <div>
                <h4 className="text-sm font-semibold text-ldaja-black mb-3">Recent Sessions</h4>
                {tokenSessions.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6">No sessions recorded yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500">
                          <th className="text-left py-2 pr-3 font-medium">When</th>
                          <th className="text-left py-2 pr-3 font-medium">Task</th>
                          <th className="text-left py-2 pr-3 font-medium">Model</th>
                          <th className="text-right py-2 pr-3 font-medium">Tokens</th>
                          <th className="text-right py-2 font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokenSessions.slice(0, 20).map((s) => (
                          <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                              {new Date(s.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                              {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-2 pr-3 text-gray-700 truncate max-w-[200px]">{s.taskDescription}</td>
                            <td className="py-2 pr-3 text-gray-500">{s.model}</td>
                            <td className="py-2 pr-3 text-right text-gray-700">{(s.inputTokens + s.outputTokens).toLocaleString()}</td>
                            <td className="py-2 text-right font-medium text-gray-800">${s.cost.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
